import React from 'react';
import cn from 'classnames';

import { LayoutVersion, Placement } from 'widget/types/common';
import type { BaseWidgetProps } from 'widget/types/widgetShell';
import { useWidgetCustomization } from 'widget/hooks/useWidgetCustomization';
import { SubmissionFormProvider } from 'widget/hooks/useSubmissionFormStore';
import { useAnimatedVisibility } from 'widget/hooks/useAnimatedVisibility';
import { ToastStoreProvider } from 'widget/hooks/useToastStore';
import { WIDGET_ID } from 'widget/consts/widget';

import styles from './BaseWidget.module.scss';
import WidgetBody from './WidgetBody';
import WidgetAside from './WidgetAside';

export type { BaseWidgetProps };

export const BaseWidget: React.FC<BaseWidgetProps> = ({
  isOpen = false,
  placement = Placement.RIGHT,
  messages,
  isTyping = false,
  hasMore = false,
  onToggle,
  onClose,
  onHideUntilReload,
  layout = LayoutVersion.DESKTOP,
  onSendMessage,
  onUploadAttachment,
  onLoadHistory,
  onRetry,
  className,
  customizationConfig,
  submissionFormStore,
  toastStore
}: BaseWidgetProps) => {
  const isMobile = layout === LayoutVersion.MOBILE;
  const { style: customStyle } = useWidgetCustomization(customizationConfig);
  const { shouldRender, isAnimatedIn } = useAnimatedVisibility(isOpen);

  const isMobileExpanded = isMobile && shouldRender;

  return (
    <SubmissionFormProvider value={submissionFormStore ?? null}>
      <ToastStoreProvider value={toastStore ?? null}>
        <div
          id={WIDGET_ID}
          className={cn(
            styles['widget'],
            placement === Placement.LEFT && styles['widget_left'],
            isMobile && styles['widget_mobile'],
            isMobileExpanded && styles['widget_mobile-open'],
            className
          )}
          style={customStyle}
        >
          {shouldRender && (
            <WidgetBody
              className={cn(
                styles['widget__body'],
                isAnimatedIn && styles['widget__body_visible']
              )}
              messages={messages}
              isTyping={isTyping}
              hasMore={hasMore}
              onClose={onClose}
              onSendMessage={onSendMessage}
              onUploadAttachment={onUploadAttachment}
              onLoadHistory={onLoadHistory}
              onRetry={onRetry}
              layout={layout}
              customizationConfig={customizationConfig}
            />
          )}
          <WidgetAside
            className={cn(
              styles['widget__aside'],
              isMobileExpanded && styles['widget__aside_hide']
            )}
            layout={layout}
            isOpen={isOpen}
            onToggle={onToggle}
            onHideUntilReload={onHideUntilReload}
            customizationConfig={customizationConfig}
          />
        </div>
      </ToastStoreProvider>
    </SubmissionFormProvider>
  );
};

export default BaseWidget;
